// Los logros: el catálogo y la cuenta que dice quién tiene cuál.
//
// ⚠ LOS `id` NO SE RENOMBRAN NUNCA. Quedan guardados en
// logros_obtenidos.logro_id; cambiar uno hace que la gente pierda el logro.
// El nombre visible, la descripción y el emoji sí se pueden cambiar.
//
// Son DERIVADOS, como el Elo: se calculan desde los hechos y se pueden tirar y
// rehacer. Por eso `calcularLogros` es pura y determinista (la fecha de un
// logro sale de la fecha del partido, no del reloj): dos corridas sobre la
// misma historia dan exactamente las mismas filas.
//
// Lo que cambia de una vez a otra (la noche, el mes, el compañero...) va en
// `contexto`, que es parte de la clave de logros_obtenidos. Así un logro se
// puede ganar varias veces sin tocar el esquema: una fila por noche, por mes o
// por compañero. Las reglas las eligió Marcos (nota del proyecto, 2026-09-24).

import { diasEntre, limitesDelMes } from './fechas';
import { resumirMes, type LineaMensual } from './mensual';
import { DIAS_EN_CARRERA } from './ranking';
import { esSetDeTiebreak } from './tiebreak';
import type { IdJugador, PartidoCalculado } from './tipos';

/**
 * Qué guarda `contexto`, para que el perfil sepa cómo mostrarlo:
 * - ninguno: se gana una vez.
 * - noche / partido: se repite; se muestra ×N con las fechas.
 * - mes / anio: uno por mes o por año; se muestra cuál.
 * - companero / rival: uno por persona; se muestra con quién o a quién.
 * - nivel: bronce, plata, oro; se muestra el más alto.
 */
export type TipoContexto = 'ninguno' | 'noche' | 'partido' | 'mes' | 'anio' | 'companero' | 'rival' | 'nivel';

export interface Logro {
  id: string;
  emoji: string;
  nombre: string;
  descripcion: string;
  contexto: TipoContexto;
  /** Los de humor van en su propia sección del perfil. */
  humor?: boolean;
}

export const LOGROS: readonly Logro[] = [
  { id: 'primera_victoria', emoji: '🥇', nombre: 'Primera victoria', descripcion: 'Ganar un partido', contexto: 'ninguno' },
  { id: 'racha_3', emoji: '🔥', nombre: 'En racha', descripcion: '3 partidos ganados seguidos', contexto: 'ninguno' },
  { id: 'racha_5', emoji: '🚀', nombre: 'Imparable', descripcion: '5 partidos ganados seguidos', contexto: 'ninguno' },
  { id: 'paliza', emoji: '🥯', nombre: 'Paliza', descripcion: 'Ganar un set 6-0', contexto: 'ninguno' },
  {
    id: 'matagigantes',
    emoji: '🗡️',
    nombre: 'Matagigantes',
    descripcion: 'Ganar con menos de 30% de chances según el Elo',
    contexto: 'ninguno',
  },
  { id: 'al_lider', emoji: '🎯', nombre: 'Al líder', descripcion: 'Ganarle a la pareja del 1º de la tabla', contexto: 'ninguno' },
  { id: 'presente_10', emoji: '🎾', nombre: 'Habitué', descripcion: '10 partidos jugados', contexto: 'ninguno' },
  { id: 'presente_25', emoji: '🏟️', nombre: 'De la casa', descripcion: '25 partidos jugados', contexto: 'ninguno' },
  { id: 'presente_50', emoji: '🏛️', nombre: 'Leyenda del club', descripcion: '50 partidos jugados', contexto: 'ninguno' },
  { id: 'campeon_mes', emoji: '👑', nombre: 'Campeón del mes', descripcion: 'Más puntos en un mes cerrado', contexto: 'mes' },
  // ── 0.10.0 ──
  { id: 'campeon_anio', emoji: '🏆', nombre: 'Campeón del año', descripcion: 'Más campeonatos del mes en el año', contexto: 'anio' },
  { id: 'noche_perfecta', emoji: '✨', nombre: 'Noche perfecta', descripcion: 'Ganar todos los partidos de la noche (3 o más)', contexto: 'noche' },
  { id: 'aplanadora', emoji: '🚜', nombre: 'Aplanadora', descripcion: 'Ganar 3 en una noche dejando al rival en 2 games o menos', contexto: 'noche' },
  { id: 'imbatible', emoji: '🛡️', nombre: 'Imbatible', descripcion: '10 partidos ganados seguidos', contexto: 'ninguno' },
  { id: 'rey_tiebreak', emoji: '🎲', nombre: 'Rey del tiebreak', descripcion: 'Sets ganados en tiebreak: 5, 10 y 20', contexto: 'nivel' },
  { id: 'dupla_oro', emoji: '🤝', nombre: 'Dupla de oro', descripcion: '5 victorias con el mismo compañero', contexto: 'companero' },
  { id: 'camaleon', emoji: '🦎', nombre: 'Camaleón', descripcion: 'Ganar con 5 compañeros distintos', contexto: 'ninguno' },
  { id: 'bestia_negra', emoji: '😈', nombre: 'Bestia negra', descripcion: '10 victorias contra el mismo rival, y ganándole el cara a cara', contexto: 'rival' },
  { id: 'en_la_cima', emoji: '⛰️', nombre: 'En la cima', descripcion: 'Terminar el mes 1º en Nivel', contexto: 'mes' },
  { id: 'siempre_presente', emoji: '📅', nombre: 'Siempre presente', descripcion: 'Jugar todas las noches del mes', contexto: 'mes' },
  { id: 'mas_subio_mes', emoji: '📈', nombre: 'El que más subió', descripcion: 'La mayor suba de puntos del mes', contexto: 'mes' },
  { id: 'mas_presente_mes', emoji: '🙋', nombre: 'El más presente', descripcion: 'El que más partidos jugó en el mes', contexto: 'mes' },
  { id: 'rosca', emoji: '🍩', nombre: 'Rosca', descripcion: 'Perder un set 6-0', contexto: 'partido', humor: true },
  { id: 'mala_racha', emoji: '🌧️', nombre: 'Mala racha', descripcion: '5 partidos perdidos seguidos', contexto: 'partido', humor: true },
];

/**
 * Sube cuando cambia la cuenta o el catálogo: el cron ve que la guardada es
 * otra y rehace todos los logros una vez, sin que nadie tenga que acordarse.
 */
export const VERSION_LOGROS = '2';

/** Debajo de esto, ganar cuenta como "Matagigantes". */
export const CHANCE_MATAGIGANTES = 0.3;
/** Los niveles de Rey del tiebreak (se guardan como texto en `contexto`). */
export const NIVELES_TIEBREAK = [5, 10, 20] as const;
export const VICTORIAS_DUPLA = 5;
export const COMPANEROS_CAMALEON = 5;
export const VICTORIAS_BESTIA = 10;
export const RACHA_IMBATIBLE = 10;
export const RACHA_MALA = 5;
/** Noche perfecta: cuántos partidos como mínimo esa noche. */
export const MINIMO_NOCHE_PERFECTA = 3;
/** Aplanadora: cuántas victorias aplastantes en la noche, y qué es aplastante. */
export const APLASTANTES_APLANADORA = 3;
export const GAMES_APLASTANTE = 2;

export interface LogroObtenido {
  jugador_id: IdJugador;
  logro_id: string;
  /** Qué es depende del logro: ver `TipoContexto`. '' para los de una vez. */
  contexto: string;
  /** El partido donde se consiguió; null para los del mes y del año. */
  partido_id: number | null;
  /** Segundos unix. Mediodía en Buenos Aires del día del partido (o del cierre). */
  obtenido_en: number;
}

const HITOS_PRESENTE: ReadonlyArray<[number, string]> = [
  [10, 'presente_10'],
  [25, 'presente_25'],
  [50, 'presente_50'],
];

/** Mediodía en Buenos Aires (UTC-3, sin horario de verano) de esa fecha. */
function mediodia(fecha: string): number {
  return Date.parse(`${fecha}T15:00:00Z`) / 1000;
}

/**
 * El campeón, el que más subió y el más presente de un mes, con la MISMA
 * regla que la pantalla "Este mes" (src/worker/rutas/mes.ts). Si el logro y la
 * pantalla dijeran cosas distintas, no se le podría creer a ninguno.
 *
 * Ojo con el campeón: es el elegible con `posicion === 1`. Si arriba de todo
 * quedó alguien que no llegó al mínimo de partidos, ese mes no hay campeón,
 * igual que en la pantalla.
 */
export function reconocimientosComoPantalla(tabla: readonly LineaMensual[]): {
  campeones: IdJugador[];
  masSubio: IdJugador[];
  masPresente: IdJugador[];
} {
  const elegibles = tabla.filter((l) => l.elegible);
  const mejores = (filas: readonly LineaMensual[], valor: (l: LineaMensual) => number) => {
    if (filas.length === 0) return [];
    const tope = Math.max(...filas.map(valor));
    return filas.filter((l) => valor(l) === tope).map((l) => l.jugador);
  };
  return {
    campeones: elegibles.filter((l) => l.posicion === 1).map((l) => l.jugador),
    masSubio: mejores(elegibles, (l) => l.delta_elo),
    masPresente: mejores(tabla, (l) => l.partidos),
  };
}

/**
 * Recorre la historia ENTERA, en orden, y devuelve todos los logros.
 *
 * Tiene que recibir todos los partidos confirmados desde el primero: las
 * rachas, los hitos y "el líder" dependen de lo que pasó antes. `mesesCerrados`
 * son los meses que ya no cambian: los logros del mes y del año salen solo de
 * esos, como el campeón.
 */
export function calcularLogros(
  calculados: readonly PartidoCalculado[],
  mesesCerrados: readonly string[],
): LogroObtenido[] {
  const obtenidos: LogroObtenido[] = [];
  const tiene = new Set<string>();

  const dar = (jugador: IdJugador, logro: string, contexto: string, partidoId: number | null, fecha: string) => {
    const llave = `${jugador}|${logro}|${contexto}`;
    if (tiene.has(llave)) return;
    tiene.add(llave);
    obtenidos.push({ jugador_id: jugador, logro_id: logro, contexto, partido_id: partidoId, obtenido_en: mediodia(fecha) });
  };

  // ── Partido a partido ──────────────────────────────────────────────────
  const jugados = new Map<IdJugador, number>();
  const racha = new Map<IdJugador, number>();
  const rachaMala = new Map<IdJugador, number>();
  const elos = new Map<IdJugador, number>();
  const tiebreaks = new Map<IdJugador, number>();
  const conCompanero = new Map<string, number>(); // "a|b" -> victorias juntos
  const companerosGanados = new Map<IdJugador, Set<IdJugador>>();
  const cara = new Map<string, { ganados: number; perdidos: number }>(); // "yo|rival"
  const caraDe = (yo: IdJugador, rival: IdJugador) => {
    const llave = `${yo}|${rival}`;
    let c = cara.get(llave);
    if (!c) cara.set(llave, (c = { ganados: 0, perdidos: 0 }));
    return c;
  };

  for (const calculado of calculados) {
    const { partido, movimientos } = calculado;
    const fecha = partido.jugado_el;
    const id = partido.id;

    // El líder ANTES de este partido: el Elo más alto entre los que ya jugaron
    // alguno. Si hay empate arriba (al principio están todos en 1000), no hay
    // líder y el logro no se puede ganar.
    let lider: IdJugador | null = null;
    let mejor = -Infinity;
    let empatado = false;
    for (const [jugador, elo] of elos) {
      if (elo > mejor) {
        mejor = elo;
        lider = jugador;
        empatado = false;
      } else if (elo === mejor) {
        empatado = true;
      }
    }
    if (empatado) lider = null;

    const ganadores = calculado.ganoA ? partido.equipoA : partido.equipoB;
    const perdedores = calculado.ganoA ? partido.equipoB : partido.equipoA;
    const chanceGanador = calculado.ganoA ? calculado.esperadaA : 1 - calculado.esperadaA;
    const perdedor = Math.min(partido.games_a, partido.games_b);
    const ganador = Math.max(partido.games_a, partido.games_b);
    const esSeisCero = perdedor === 0 && ganador >= 6;
    const conTiebreak = esSetDeTiebreak(partido.games_a, partido.games_b);

    for (const j of ganadores) {
      const companero = ganadores[0] === j ? ganadores[1] : ganadores[0];

      dar(j, 'primera_victoria', '', id, fecha);
      const r = (racha.get(j) ?? 0) + 1;
      racha.set(j, r);
      rachaMala.set(j, 0);
      if (r >= 3) dar(j, 'racha_3', '', id, fecha);
      if (r >= 5) dar(j, 'racha_5', '', id, fecha);
      if (r >= RACHA_IMBATIBLE) dar(j, 'imbatible', '', id, fecha);
      if (esSeisCero) dar(j, 'paliza', '', id, fecha);
      if (chanceGanador < CHANCE_MATAGIGANTES) dar(j, 'matagigantes', '', id, fecha);
      if (lider !== null && perdedores.includes(lider)) dar(j, 'al_lider', '', id, fecha);

      if (conTiebreak) {
        const t = (tiebreaks.get(j) ?? 0) + 1;
        tiebreaks.set(j, t);
        for (const nivel of NIVELES_TIEBREAK) if (t === nivel) dar(j, 'rey_tiebreak', String(nivel), id, fecha);
      }

      const juntos = (conCompanero.get(`${j}|${companero}`) ?? 0) + 1;
      conCompanero.set(`${j}|${companero}`, juntos);
      if (juntos >= VICTORIAS_DUPLA) dar(j, 'dupla_oro', String(companero), id, fecha);

      const distintos = companerosGanados.get(j) ?? new Set<IdJugador>();
      distintos.add(companero);
      companerosGanados.set(j, distintos);
      if (distintos.size >= COMPANEROS_CAMALEON) dar(j, 'camaleon', '', id, fecha);

      for (const rival of perdedores) {
        const c = caraDe(j, rival);
        c.ganados += 1;
        // Se gana en el momento en que se cumplen las dos cosas, y no se
        // pierde si después el rival da vuelta el cara a cara.
        if (c.ganados >= VICTORIAS_BESTIA && c.ganados > c.perdidos) {
          dar(j, 'bestia_negra', String(rival), id, fecha);
        }
      }
    }

    for (const j of perdedores) {
      racha.set(j, 0);
      const m = (rachaMala.get(j) ?? 0) + 1;
      rachaMala.set(j, m);
      // Exactamente al llegar: para volver a ganarla hay que cortar la racha.
      if (m === RACHA_MALA) dar(j, 'mala_racha', String(id), id, fecha);
      if (esSeisCero) dar(j, 'rosca', String(id), id, fecha);
      for (const rival of ganadores) caraDe(j, rival).perdidos += 1;
    }

    for (const m of movimientos) {
      const n = (jugados.get(m.jugador) ?? 0) + 1;
      jugados.set(m.jugador, n);
      elos.set(m.jugador, m.despues);
      for (const [hito, logro] of HITOS_PRESENTE) if (n === hito) dar(m.jugador, logro, '', id, fecha);
    }
  }

  // ── Noche a noche ──────────────────────────────────────────────────────
  const porNoche = new Map<string, PartidoCalculado[]>();
  for (const c of calculados) {
    const lista = porNoche.get(c.partido.jugado_el) ?? [];
    lista.push(c);
    porNoche.set(c.partido.jugado_el, lista);
  }
  for (const [noche, partidos] of porNoche) {
    const cuenta = new Map<IdJugador, { jugados: number; ganados: number; aplastantes: number; ultimo: number }>();
    for (const c of partidos) {
      const aplastante = Math.min(c.partido.games_a, c.partido.games_b) <= GAMES_APLASTANTE;
      const ganadores = c.ganoA ? c.partido.equipoA : c.partido.equipoB;
      for (const m of c.movimientos) {
        const f = cuenta.get(m.jugador) ?? { jugados: 0, ganados: 0, aplastantes: 0, ultimo: 0 };
        const gano = ganadores.includes(m.jugador);
        f.jugados += 1;
        f.ultimo = c.partido.id;
        if (gano) f.ganados += 1;
        if (gano && aplastante) f.aplastantes += 1;
        cuenta.set(m.jugador, f);
      }
    }
    for (const [j, f] of cuenta) {
      if (f.jugados >= MINIMO_NOCHE_PERFECTA && f.ganados === f.jugados) {
        dar(j, 'noche_perfecta', noche, f.ultimo, noche);
      }
      if (f.aplastantes >= APLASTANTES_APLANADORA) dar(j, 'aplanadora', noche, f.ultimo, noche);
    }
  }

  // ── Mes a mes (solo los cerrados) ──────────────────────────────────────
  const cerrados = [...mesesCerrados].sort();
  const campeonatos: Array<{ mes: string; campeones: IdJugador[]; tabla: LineaMensual[] }> = [];
  // Para "En la cima": el Elo y el último partido de cada uno a fin de mes.
  const alCierre = new Map<IdJugador, { elo: number; ultimo: string }>();
  let i = 0;

  for (const mes of cerrados) {
    const { hasta } = limitesDelMes(mes);
    while (i < calculados.length && calculados[i]!.partido.jugado_el <= hasta) {
      const c = calculados[i]!;
      for (const m of c.movimientos) alCierre.set(m.jugador, { elo: m.despues, ultimo: c.partido.jugado_el });
      i += 1;
    }

    const delMes = calculados.filter((c) => c.partido.jugado_el.slice(0, 7) === mes);
    if (delMes.length === 0) continue;

    const { tabla } = resumirMes(mes, delMes);
    const r = reconocimientosComoPantalla(tabla);
    for (const j of r.campeones) dar(j, 'campeon_mes', mes, null, hasta);
    for (const j of r.masSubio) dar(j, 'mas_subio_mes', mes, null, hasta);
    for (const j of r.masPresente) dar(j, 'mas_presente_mes', mes, null, hasta);
    campeonatos.push({ mes, campeones: r.campeones, tabla });

    // Siempre presente: todas las noches con partidos de ese mes.
    const noches = new Set(delMes.map((c) => c.partido.jugado_el));
    const nochesDe = new Map<IdJugador, Set<string>>();
    for (const c of delMes) {
      for (const m of c.movimientos) {
        const s = nochesDe.get(m.jugador) ?? new Set<string>();
        s.add(c.partido.jugado_el);
        nochesDe.set(m.jugador, s);
      }
    }
    for (const [j, s] of nochesDe) if (s.size === noches.size) dar(j, 'siempre_presente', mes, null, hasta);

    // En la cima: el 1º de la tabla general el último día del mes, entre los
    // que jugaron en los DIAS_EN_CARRERA anteriores (la regla de la tabla).
    let tope = -Infinity;
    let arriba: IdJugador[] = [];
    for (const [j, f] of alCierre) {
      if (diasEntre(f.ultimo, hasta) > DIAS_EN_CARRERA) continue;
      if (f.elo > tope) {
        tope = f.elo;
        arriba = [j];
      } else if (f.elo === tope) {
        arriba.push(j);
      }
    }
    for (const j of arriba) dar(j, 'en_la_cima', mes, null, hasta);
  }

  // ── Año a año (cuando cerró diciembre) ─────────────────────────────────
  const anios = new Set(cerrados.filter((m) => m.endsWith('-12')).map((m) => m.slice(0, 4)));
  for (const anio of anios) {
    const delAnio = campeonatos.filter((c) => c.mes.startsWith(`${anio}-`));
    const titulos = new Map<IdJugador, number>();
    const puntos = new Map<IdJugador, number>();
    for (const c of delAnio) {
      for (const j of c.campeones) titulos.set(j, (titulos.get(j) ?? 0) + 1);
      for (const l of c.tabla) puntos.set(l.jugador, (puntos.get(l.jugador) ?? 0) + l.puntos);
    }
    if (titulos.size === 0) continue;
    const masTitulos = Math.max(...titulos.values());
    const empatados = [...titulos].filter(([, n]) => n === masTitulos).map(([j]) => j);
    // Desempata por los puntos del año en la tabla mensual; si siguen iguales, lo comparten.
    const masPuntos = Math.max(...empatados.map((j) => puntos.get(j) ?? 0));
    for (const j of empatados) {
      if ((puntos.get(j) ?? 0) === masPuntos) dar(j, 'campeon_anio', anio, null, `${anio}-12-31`);
    }
  }

  return obtenidos;
}

// ── El 1: el título que rota ─────────────────────────────────────────────

export interface ReinadoEl1 {
  jugador_id: IdJugador;
  /** El mes cuyo cierre le dio el título. */
  desde: string;
  /** El mes cuyo cierre se lo sacó; null si lo tiene ahora. */
  hasta: string | null;
}

export interface TituloEl1 {
  /** Quién es El 1 ahora; null si está vacante (empate arriba, o todavía no hubo campeón). */
  actual: IdJugador | null;
  campeonatos: Map<IdJugador, number>;
  reinados: ReinadoEl1[];
  /** Cada vez que el título cambió de dueño (o quedó vacante), en orden. */
  cambios: Array<{ mes: string; jugador_id: IdJugador | null }>;
}

/**
 * "El 1" lo tiene quien acumula más campeonatos del mes. Uno solo: si hay
 * empate arriba, queda vacante hasta que alguno supere a los demás. Una corona
 * compartida suma un campeonato a cada campeón.
 *
 * Se calcula al vuelo desde los campeones de cada mes cerrado (los logros
 * `campeon_mes`), recorriéndolos en orden: no hay un estado guardado que se
 * pueda desincronizar.
 */
export function tituloEl1(campeonesPorMes: ReadonlyArray<{ mes: string; jugador_id: IdJugador }>): TituloEl1 {
  const porMes = new Map<string, IdJugador[]>();
  for (const c of campeonesPorMes) porMes.set(c.mes, [...(porMes.get(c.mes) ?? []), c.jugador_id]);

  const campeonatos = new Map<IdJugador, number>();
  const reinados: ReinadoEl1[] = [];
  const cambios: TituloEl1['cambios'] = [];
  let actual: IdJugador | null = null;

  for (const mes of [...porMes.keys()].sort()) {
    for (const j of porMes.get(mes)!) campeonatos.set(j, (campeonatos.get(j) ?? 0) + 1);

    const tope = Math.max(...campeonatos.values());
    const arriba = [...campeonatos].filter(([, n]) => n === tope).map(([j]) => j);
    const nuevo = arriba.length === 1 ? arriba[0]! : null;
    if (nuevo === actual) continue;

    const abierto = reinados.find((r) => r.hasta === null);
    if (abierto) abierto.hasta = mes;
    if (nuevo !== null) reinados.push({ jugador_id: nuevo, desde: mes, hasta: null });
    cambios.push({ mes, jugador_id: nuevo });
    actual = nuevo;
  }

  return { actual, campeonatos, reinados, cambios };
}
